import { useState } from "react";
import { Button } from "@/components/ui/button";

// Badge definitions with full details
const BADGE_INFO = {
  founder: {
    label: "Founder",
    icon: "👑",
    color: "from-yellow-400 via-yellow-500 to-amber-600",
    textColor: "text-black",
    description: "Platform founder and creator of RGM Studio.",
    howToEarn: "This badge is reserved for the RGM Studio founding team.",
    benefits: ["Platform admin access", "Priority support", "Featured placement"]
  },
  founding_artist: {
    label: "Founding Artist",
    icon: "⭐",
    color: "from-purple-500 to-pink-600",
    textColor: "text-white",
    description: "One of the first artists to join RGM Studio during our launch phase.",
    howToEarn: "Awarded to artists who joined during the platform's beta and early launch period.",
    benefits: ["Permanent recognition badge", "Early adopter status", "Priority feature consideration"]
  },
  spotlight_winner: {
    label: "Spotlight Winner",
    icon: "🏆",
    color: "from-green-500 to-emerald-600",
    textColor: "text-white",
    description: "Won a top 3 position in RGM Studio's Weekly Spotlight competition.",
    howToEarn: "Submit high-quality music and earn a top 3 spot selected by our A&R team each week.",
    benefits: ["Homepage feature", "Increased visibility", "Press coverage eligibility"]
  },
  spotlight_artist: {
    label: "Spotlight Artist",
    icon: "🔥",
    color: "from-orange-500 to-red-500",
    textColor: "text-white",
    description: "Track featured in a weekly or monthly RGM Studio spotlight.",
    howToEarn: "Have your track selected for spotlight placement by our editorial team.",
    benefits: ["Discovery page feature", "Algorithm boost", "Community recognition"]
  },
  playlist_pick: {
    label: "Playlist Pick",
    icon: "🎵",
    color: "from-cyan-500 to-blue-500",
    textColor: "text-white",
    description: "Track added to an official RGM Studio curated playlist.",
    howToEarn: "Create quality music that fits our curated playlist themes and genres.",
    benefits: ["Playlist exposure", "New listener reach", "Cross-promotion"]
  },
  rising_artist: {
    label: "Rising Artist",
    icon: "📈",
    color: "from-green-400 to-emerald-500",
    textColor: "text-white",
    description: "Showing significant growth in plays, likes, and engagement.",
    howToEarn: "Consistently grow your listener base and engagement metrics on the platform.",
    benefits: ["Trending section feature", "Growth recognition", "A&R attention"]
  },
  verified_artist: {
    label: "Verified Artist",
    icon: "✓",
    color: "from-blue-500 to-blue-600",
    textColor: "text-white",
    description: "Identity and artistry verified by the RGM Studio team.",
    howToEarn: "Request verification with a complete profile, social presence, and multiple approved tracks.",
    benefits: ["Blue checkmark badge", "Priority support", "Trust signal for listeners"]
  },
  admin: {
    label: "Admin",
    icon: "⚡",
    color: "from-purple-600 to-purple-700",
    textColor: "text-white",
    description: "RGM Studio platform administrator.",
    howToEarn: "This badge is reserved for RGM Studio staff and administrators.",
    benefits: ["Full platform access", "Content moderation", "Artist management"]
  },
  ar: {
    label: "A&R",
    icon: "🎯",
    color: "from-pink-500 to-red-500",
    textColor: "text-white",
    description: "RGM Studio Artists & Repertoire — the team that scouts and features tracks.",
    howToEarn: "Invited by RGM Studio admins. A&R team members help curate Featured and Spotlight picks.",
    benefits: ["Feature tracks", "Suggest Spotlight Top 3", "Priority review queue"]
  },
  producer: {
    label: "Producer",
    icon: "🎧",
    color: "from-blue-600 to-blue-700",
    textColor: "text-white",
    description: "Verified music producer on RGM Studio.",
    howToEarn: "Register as a producer and have your credentials verified by our team.",
    benefits: ["Producer badge display", "Beat marketplace access", "Collaboration features"]
  },
  community_builder: {
    label: "Community Builder",
    icon: "🏗️",
    color: "from-blue-600 to-blue-700",
    textColor: "text-white",
    description: "Referred 3+ artists to join RGM Studio.",
    howToEarn: "Share your referral link and help grow the RGM community.",
    benefits: ["Referral recognition", "Community leader status"]
  },
  talent_scout: {
    label: "Talent Scout",
    icon: "🔍",
    color: "from-green-600 to-green-700",
    textColor: "text-white",
    description: "Referred 10+ artists to join RGM Studio.",
    howToEarn: "Continue sharing your referral link and bringing in new talent.",
    benefits: ["Talent scout recognition", "Special community access"]
  },
  rgm_ambassador: {
    label: "RGM Ambassador",
    icon: "🌟",
    color: "from-purple-500 to-pink-600",
    textColor: "text-white",
    description: "Referred 25+ artists to join RGM Studio.",
    howToEarn: "Become a top referrer by bringing 25+ new artists to the platform.",
    benefits: ["Ambassador status", "Exclusive features", "Direct team access"]
  },
  ar_connector: {
    label: "A&R Connector",
    icon: "🎯",
    color: "from-yellow-500 to-amber-600",
    textColor: "text-black",
    description: "Referred 50+ artists to join RGM Studio.",
    howToEarn: "Achieve legendary status by referring 50+ new artists.",
    benefits: ["Top referrer recognition", "Industry networking", "VIP status"]
  },
  crew_seeder: {
    label: "Crew Seeder",
    icon: "🌱",
    color: "from-emerald-500 to-green-600",
    textColor: "text-white",
    description: "Brought 3+ artists into RGM Studio crews via shared invite links.",
    howToEarn: "Share any crew using the Share button and earn this badge once 3+ artists join through your link.",
    benefits: ["Crew Seeder badge display", "Recognition on crew member lists", "Growth leaderboard visibility"]
  }
};

// Clickable Badge Component
export const BadgeWithModal = ({ type, size = "default", showLabel = true }) => {
  const [showModal, setShowModal] = useState(false);
  const badge = BADGE_INFO[type];
  
  if (!badge) return null;

  const sizeClasses = {
    small: "px-2 py-0.5 text-xs",
    default: "px-3 py-1 text-sm",
    large: "px-4 py-1.5 text-base"
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowModal(true);
        }}
        className={`rounded-full font-bold inline-flex items-center gap-1 bg-gradient-to-r ${badge.color} ${badge.textColor} ${sizeClasses[size]} hover:opacity-90 transition-opacity cursor-pointer`}
      >
        <span>{badge.icon}</span>
        {showLabel && badge.label}
      </button>

      {/* Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Badge Header */}
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${badge.color} flex items-center justify-center text-3xl`}>
                {badge.icon}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{badge.label}</h2>
                <span className={`px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r ${badge.color} ${badge.textColor}`}>
                  Badge
                </span>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <p className="text-gray-300">{badge.description}</p>
            </div>

            {/* How to Earn */}
            <div className="bg-purple-900/20 rounded-xl p-4 mb-6">
              <h3 className="text-sm font-semibold text-purple-400 mb-2">How to Earn</h3>
              <p className="text-gray-300 text-sm">{badge.howToEarn}</p>
            </div>

            {/* Benefits */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-400 mb-2">Benefits</h3>
              <ul className="space-y-2">
                {badge.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-2 text-gray-300 text-sm">
                    <span className="text-green-400">✓</span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            {/* Close Button */}
            <Button
              onClick={() => setShowModal(false)}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

// Simple Badge List Display (for profile pages)
export const BadgeList = ({ badges = [], size = "default" }) => {
  if (!badges || badges.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-2">
      {badges.map((badgeType) => (
        <BadgeWithModal key={badgeType} type={badgeType} size={size} />
      ))}
    </div>
  );
};

// Badge Info for static displays (no modal)
export const getBadgeInfo = (type) => {
  return BADGE_INFO[type] || null;
};

export default BadgeWithModal;
